import React, { useState, useCallback, useRef } from "react";
import Cropper from "react-easy-crop";
import { useSearchParams, useNavigate } from "react-router-dom";

function getCroppedImg(
  imageSrc,
  croppedAreaPixels,
  zoom,
  aspect,
  stretch = false,
  outputWidth = 400,
  outputHeight = 400,
) {
  // Crop the image using the pixel area from react-easy-crop
  return new Promise((resolve, reject) => {
    const image = new window.Image();
    image.src = imageSrc;
    image.onload = () => {
      // 1. Crop the selected area at full resolution
      let cropCanvas = document.createElement("canvas");
      cropCanvas.width = croppedAreaPixels.width;
      cropCanvas.height = croppedAreaPixels.height;
      let cropCtx = cropCanvas.getContext("2d");
      cropCtx.imageSmoothingEnabled = true;
      cropCtx.imageSmoothingQuality = "high";

      cropCtx.drawImage(
        image,
        croppedAreaPixels.x,
        croppedAreaPixels.y,
        croppedAreaPixels.width,
        croppedAreaPixels.height,
        0,
        0,
        croppedAreaPixels.width,
        croppedAreaPixels.height,
      );
      // 2. Progressive downscaling to output size (always, for best quality)
      let tmpCanvas = cropCanvas;
      let tmpW = cropCanvas.width;
      let tmpH = cropCanvas.height;
      while (tmpW > outputWidth * 2 || tmpH > outputHeight * 2) {
        tmpW = Math.max(outputWidth, Math.floor(tmpW / 2));
        tmpH = Math.max(outputHeight, Math.floor(tmpH / 2));
        const stepCanvas = document.createElement("canvas");
        stepCanvas.width = tmpW;
        stepCanvas.height = tmpH;
        const stepCtx = stepCanvas.getContext("2d");
        stepCtx.imageSmoothingEnabled = true;
        stepCtx.imageSmoothingQuality = "high";
        stepCtx.drawImage(
          tmpCanvas,
          0,
          0,
          tmpCanvas.width,
          tmpCanvas.height,
          0,
          0,
          tmpW,
          tmpH,
        );
        tmpCanvas = stepCanvas;
      }
      // 3. Final draw to output size (stretch or not, always high quality)
      let finalCanvas = document.createElement("canvas");
      finalCanvas.width = outputWidth;
      finalCanvas.height = outputHeight;
      let finalCtx = finalCanvas.getContext("2d");
      finalCtx.imageSmoothingEnabled = true;
      finalCtx.imageSmoothingQuality = "high";
      finalCtx.drawImage(
        tmpCanvas,
        0,
        0,
        tmpCanvas.width,
        tmpCanvas.height,
        0,
        0,
        outputWidth,
        outputHeight,
      );
      // 4. Output as PNG
      finalCanvas.toBlob((blob) => {
        if (!blob) {
          reject(new Error("Canvas is empty"));
          return;
        }
        blob.name = "Logo.png";
        resolve(blob);
      }, "image/png"); // Always use PNG for transparency
    };
    image.onerror = (e) => reject(e);
  });
}

const ImageCropper = () => {
  const [searchParams] = useSearchParams();
  const enterpriseID = searchParams.get("enterpriseID") || ""; // Get FabSurveyID from URL
  const navigate = useNavigate();
  // If enterpriseID is missing, redirect or show error
  React.useEffect(() => {
    if (!enterpriseID) {
      showNotification("Missing required enterpriseID in URL.");
      // Optionally, redirect to a default or error page:
      // navigate("/someErrorPage");
    }
  }, [enterpriseID, navigate]);

  const [imageSrc, setImageSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
  const [croppedImage, setCroppedImage] = useState(null);
  const [stretch, setStretch] = useState(false);
  const [designerWidth, setDesignerWidth] = useState(400);
  const [designerHeight, setDesignerHeight] = useState(400);
  const [logoType, setLogoType] = useState("Logo");
  const [altLogoName, setAltLogoName] = useState("");
  const cropperSize = 400; // px, must match the style below
  const minZoom = 0.3;
  const submitURL =
    process.env.NODE_ENV === "production"
      ? `https://actionflowofficialapi-bma8avfpabbvfxa0.centralus-01.azurewebsites.net/api/internalControls/files/sendLogo?enterpriseID=${enterpriseID}`
      : `https://localhost:7021/api/internalControls/files/sendLogo?enterpriseID=${enterpriseID}`;

  // Notification (toast) state
  const [notification, setNotification] = useState("");
  const showNotification = (msg) => {
    setNotification(msg);
    setTimeout(() => setNotification(""), 3000);
  };

  // Set initial zoom to fit the image inside the crop area, but allow zooming out
  const onImageLoaded = useCallback((image) => {
    const width = image.naturalWidth;
    const height = image.naturalHeight;
    const zoomW = cropperSize / width;
    const zoomH = cropperSize / height;
    const initialZoom = Math.min(1, Math.min(zoomW, zoomH));
    setZoom(initialZoom);
    return false;
  }, []);

  const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
    setCroppedAreaPixels(croppedAreaPixels);
  }, []);

  const onSelectFile = (event) => {
    if (event.target.files && event.target.files.length > 0) {
      const reader = new FileReader();
      reader.readAsDataURL(event.target.files[0]);
      reader.onload = () => {
        const img = new window.Image();
        img.src = reader.result;
        img.onload = () => {
          const width = img.naturalWidth;
          const height = img.naturalHeight;
          const aspect = width / height;
          let targetW, targetH;
          // Decide ratio: square if close to 1:1, else 2.5:1 (landscape) or 1:2.5 (portrait)
          if (aspect > 0.8 && aspect < 1.2) {
            // Square
            targetW = targetH = Math.max(width, height);
          } else if (aspect >= 1.2) {
            // Wide: 2.5:1
            targetW = Math.max(width, height * 2.5);
            targetH = targetW / 2.5;
          } else {
            // Tall: 1:2.5
            targetH = Math.max(height, width * 2.5);
            targetW = targetH / 2.5;
          }
          setDesignerWidth(Math.round(targetW));
          setDesignerHeight(Math.round(targetH));
          const canvas = document.createElement("canvas");
          canvas.width = Math.round(targetW);
          canvas.height = Math.round(targetH);
          const ctx = canvas.getContext("2d");
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          // Center the image
          const dx = (canvas.width - width) / 2;
          const dy = (canvas.height - height) / 2;
          ctx.drawImage(img, dx, dy, width, height);
          setImageSrc(canvas.toDataURL("image/png"));
        };
      };
    }
  };

  // Helper to determine output size for cropping
  function getOutputSize() {
    const aspect = designerWidth / designerHeight;
    if (aspect > 0.8 && aspect < 1.2) {
      // Square
      return { width: 96, height: 96 };
    } else if (aspect >= 1.2) {
      // Wide rectangle (2.5:1)
      return { width: 240, height: 96 };
    } else {
      // Tall rectangle (1:2.5)
      return { width: 96, height: 240 };
    }
  }

  const showCroppedImage = useCallback(async () => {
    try {
      const { width: outW, height: outH } = getOutputSize();
      const croppedImgBlob = await getCroppedImg(
        imageSrc,
        croppedAreaPixels,
        zoom,
        1, // aspect ratio
        true, // always stretch to output size
        outW, // output width
        outH, // output height
      );
      setCroppedImage(URL.createObjectURL(croppedImgBlob));
    } catch (e) {
      console.error(e);
    }
  }, [
    imageSrc,
    croppedAreaPixels,
    zoom,
    stretch,
    designerWidth,
    designerHeight,
  ]);

  // Automatically update cropped image preview on crop/zoom/image change
  React.useEffect(() => {
    if (imageSrc && croppedAreaPixels) {
      showCroppedImage();
    }
  }, [
    imageSrc,
    croppedAreaPixels,
    zoom,
    stretch,
    designerWidth,
    designerHeight,
  ]);

  const submitCroppedImage = useCallback(async () => {
    try {
      const { width: outW, height: outH } = getOutputSize();
      const croppedImgBlob = await getCroppedImg(
        imageSrc,
        croppedAreaPixels,
        zoom,
        1, // aspect ratio
        true, // always stretch to output size
        outW, // output width
        outH, // output height
      );
      const formData = new FormData();
      let blobName = "Logo.png";
      if (logoType === "Alternate Logo" && altLogoName.trim()) {
        blobName = altLogoName.trim().replace(/\s+/g, "_") + ".png";
      }
      formData.append("file", croppedImgBlob, blobName);
      const response = await fetch(submitURL, {
        method: "POST",
        body: formData,
      });
      if (response.ok) {
        showNotification("Logo submitted successfully!");
      } else {
        showNotification("Failed to submit logo.");
      }
    } catch (e) {
      console.error(e);
      showNotification("Error submitting logo.");
    }
  }, [
    imageSrc,
    croppedAreaPixels,
    zoom,
    stretch,
    designerWidth,
    designerHeight,
    logoType,
    altLogoName,
  ]);

  // Helper to set zoom so image fills the crop area
  const fillGrid = useCallback(() => {
    if (!imageSrc) return;
    const img = new window.Image();
    img.src = imageSrc;
    img.onload = () => {
      const width = img.naturalWidth;
      const height = img.naturalHeight;
      // Calculate zoom so the image fully covers the crop area (like CSS background-size: cover)
      const zoomW = 1;
      const zoomH = 1;
      const fillZoom = Math.max(zoomW, zoomH);
      setZoom(fillZoom);
      // Center the image in the crop area
      // Calculate offset so the image is centered after zoom
      const offsetX = 0;
      const offsetY = 0;
      setCrop({ x: offsetX, y: offsetY });
      // Only update croppedAreaPixels if it exists and has valid x/y
      if (
        croppedAreaPixels &&
        typeof croppedAreaPixels.x === "number" &&
        typeof croppedAreaPixels.y === "number"
      ) {
        croppedAreaPixels.x = offsetX;
        croppedAreaPixels.y = offsetY;
        croppedAreaPixels.width = img.naturalWidth;
        croppedAreaPixels.height = img.naturalHeight;
        setCroppedAreaPixels(croppedAreaPixels);
      } else if (!croppedAreaPixels) {
        // If null, initialize and set
        const newCropped = {
          x: offsetX,
          y: offsetY,
          width: img.naturalWidth,
          height: img.naturalHeight,
        };
        setCroppedAreaPixels(newCropped);
      }
      // Always update preview after fillGrid
      setTimeout(() => showCroppedImage(), 0);
    };
  }, [imageSrc, cropperSize, croppedAreaPixels, showCroppedImage]);

  // Only allow letters, numbers, and spaces in Alternate Logo name, and limit to 50 chars
  const handleAltLogoNameChange = (e) => {
    let value = e.target.value.replace(/[^a-zA-Z0-9 ]/g, "");
    if (value.length > 50) value = value.slice(0, 50);
    setAltLogoName(value);
  };

  return (
    <div style={{ display: "flex", alignItems: "flex-start", gap: 32 }}>
      {/* Notification Toast */}
      {notification && (
        <div
          style={{
            position: "fixed",
            top: 24,
            left: "50%",
            transform: "translateX(-50%)",
            background: "#073e64",
            color: "#fff",
            padding: "12px 32px",
            borderRadius: 8,
            fontWeight: 600,
            fontSize: 16,
            boxShadow: "0 2px 8px rgba(0,0,0,0.12)",
            zIndex: 9999,
            letterSpacing: 0.2,
          }}
        >
          {notification}
        </div>
      )}
      {/* Controls container */}
      <div
        style={{
          minWidth: 200,
          maxWidth: 250,
          padding: 24,
          background: "#073e64",
          borderRadius: 12,
          boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
          display: "flex",
          flexDirection: "column",
          gap: 24,
          alignSelf: "flex-start", // Align to top
        }}
      >
        <div>
          <label
            style={{
              fontWeight: 600,
              marginBottom: 8,
              display: "block",
              color: "#4ba0c7",
            }}
          >
            Upload Image
          </label>
          <div>
            <label
              htmlFor="file-upload"
              style={{
                display: "inline-block",
                padding: "8px 0",
                borderRadius: 6,
                background: "#4ba0c7",
                color: "#fff",
                fontSize: 14,
                fontWeight: 600,
                cursor: "pointer",
                width: "100%",
                textAlign: "center",
                marginBottom: 8,
              }}
            >
              Choose File
            </label>
            <input
              id="file-upload"
              type="file"
              accept="image/*"
              onChange={onSelectFile}
              style={{ display: "none" }}
            />
          </div>
        </div>
        {imageSrc && (
          <div>
            <label
              style={{
                fontWeight: 600,
                marginBottom: 8,
                display: "block",
                color: "#4ba0c7",
              }}
            >
              Zoom
            </label>
            <input
              type="range"
              min={minZoom}
              max={3}
              step={0.01}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              style={{
                width: "100%",
                accentColor: "#4ba0c7", // Modern browsers
                // Fallback for older browsers:
                WebkitAppearance: "none",
                height: 4,
                borderRadius: 2,
                background: "#e0e7ef",
                outline: "none",
                margin: 0,
                // Custom thumb for Webkit
                ...{
                  "::-webkit-slider-thumb": {
                    WebkitAppearance: "none",
                    appearance: "none",
                    width: 18,
                    height: 18,
                    borderRadius: "50%",
                    background: "#4ba0c7",
                    cursor: "pointer",
                    border: "2px solid #fff",
                    boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
                  },
                },
              }}
            />
          </div>
        )}
        {imageSrc && (
          <button
            onClick={fillGrid}
            style={{
              marginTop: 8,
              padding: "8px 0",
              borderRadius: 6,
              background: "#4ba0c7",
              border: "none",
              fontWeight: 600,
              cursor: "pointer",
              color: "#fff", // White text
            }}
          >
            Center & Fill
          </button>
        )}
        {imageSrc && (
          <div style={{ marginBottom: 16 }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                marginBottom: 8,
              }}
            >
              <div
                style={{
                  display: "flex",
                  background: "#174e7a",
                  borderRadius: 6,
                  overflow: "hidden",
                }}
              >
                <button
                  type="button"
                  onClick={() => setLogoType("Logo")}
                  style={{
                    background: logoType === "Logo" ? "#4ba0c7" : "transparent",
                    color: logoType === "Logo" ? "#fff" : "#b0cbe2",
                    border: "none",
                    padding: "6px 16px",
                    fontWeight: 600,
                    cursor: "pointer",
                    outline: "none",
                    transition: "background 0.2s",
                  }}
                >
                  Logo
                </button>
                <button
                  type="button"
                  onClick={() => setLogoType("Alternate Logo")}
                  style={{
                    background:
                      logoType === "Alternate Logo" ? "#4ba0c7" : "transparent",
                    color: logoType === "Alternate Logo" ? "#fff" : "#b0cbe2",
                    border: "none",
                    padding: "6px 16px",
                    fontWeight: 600,
                    cursor: "pointer",
                    outline: "none",
                    transition: "background 0.2s",
                  }}
                >
                  Alternate Logo
                </button>
              </div>
            </div>
            {logoType === "Alternate Logo" && (
              <input
                type="text"
                placeholder="Alternate Logo Name"
                value={altLogoName}
                onChange={handleAltLogoNameChange}
                maxLength={50}
                style={{
                  width: "100%",
                  padding: "7px 10px",
                  borderRadius: 5,
                  border: "1px solid #4ba0c7",
                  fontSize: 14,
                  marginTop: 4,
                  marginBottom: 4,
                  outline: "none",
                }}
              />
            )}
          </div>
        )}
        {croppedImage && (
          <div style={{ marginTop: 24, textAlign: "center" }}>
            <img
              src={croppedImage}
              alt="Cropped"
              style={{
                maxWidth: 120,
                maxHeight: 120,
                boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
              }}
            />
            <div style={{ marginTop: 12 }}>
              <button
                onClick={submitCroppedImage}
                style={{
                  padding: "6px 18px",
                  borderRadius: 6,
                  background: "#99d03a",
                  color: "#fff",
                  border: "none",
                  fontWeight: 600,
                  cursor: "pointer",
                }}
              >
                Submit Logo
              </button>
            </div>
          </div>
        )}
      </div>
      {/* Cropper grid and preview */}
      <div style={{ width: cropperSize }}>
        <h1
          style={{
            color: "#073e64",
            fontWeight: 700,
            fontSize: 28,
            margin: "0 0 24px 0",
            textAlign: "left",
          }}
        >
          Import Logo
        </h1>
        {imageSrc && (
          <div
            style={{
              position: "relative",
              width: cropperSize,
              height: cropperSize,
              background: "transparent",
              overflow: "hidden", // Ensure cropper respects rounded corners
              boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
            }}
          >
            <Cropper
              image={imageSrc}
              crop={crop}
              zoom={zoom}
              aspect={designerWidth / designerHeight}
              minZoom={minZoom}
              maxZoom={5}
              restrictPosition={false}
              zoomSpeed={0.1}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
              onMediaLoaded={onImageLoaded}
              style={{
                containerStyle: { background: "rgba(0, 0, 0, 0.6)" },
                cropAreaStyle: {
                  background: "transparent",
                  border: "2px solid #fff",
                },
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ImageCropper;
